/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import flattenDeep from 'lodash/flattenDeep';
import uniq from 'lodash/uniq';
import {
    Box,
    Heading,
    Spacer,
    Carousel,
    useWindowSize,
    Icon,
    Button,
    SearchActions,
} from 'sarsaparilla';
import {
    fetchTicketSearchSelector,
    fetchActivityPassesSearchSelector,
    fetchTreePermitsSearchSelector,
    fetchVenueReservationSelector,
    fetchVenueFacilitySelector,
} from '../redux/reducers/fetchSearch';
import ActivitiesExperiencesItem from '../components/ActivitiesExperiencesItem';
import { SearchProps } from '../props/search';

const propTypes = {
    location: PropTypes.shape({
        locationId: PropTypes.string,
        name: PropTypes.string,
    }),
    search: SearchProps,
    searchActivityPasses: SearchProps,
    searchTreePermits: SearchProps,
    navToSearch: PropTypes.func,
    searchVenues: PropTypes.any,
    searchVenueFacilities: PropTypes.any,
};

function ActivitiesExperiences({
    search,
    searchActivityPasses,
    searchTreePermits,
    searchVenues,
    searchVenueFacilities,
    location,
    navToSearch,
}) {
    const { width } = useWindowSize();
    const activityPasses = searchActivityPasses.data?.results || [];
    const tickets = search.data?.results || [];
    const treePermit = searchTreePermits.data?.results || [];
    const venuesData = searchVenues.data?.results || [];
    const venueFacilities = searchVenueFacilities?.data?.results || [];

    const venues = venueFacilities.map((item) => {
        const current = venuesData
            .filter((v) => v.parent_id === item.entity_id)
            .map((v) => v.venue_tags);
        if (current.length > 0) {
            const tags = flattenDeep(current);
            return {
                ...item,
                venue_tags: uniq(tags),
            };
        }
        return item;
    });
    const joinResults = [...tickets, ...activityPasses, ...treePermit, ...venues];
    joinResults.sort((a, b) => a.name.localeCompare(b.name));

    if (joinResults.length < 1) {
        return null;
    }

    return (
        <Box paddingY="xl" id="activities-wrapper-id">
            <Heading headingLevel={2} appearance="h3">
                Activities & Experiences
            </Heading>
            <Spacer size="gutter" />
            {joinResults.length > 0 && (
                <Carousel
                    slideTargetWidth={275}
                    hasNegativeMargins={width > 1240}
                    hasPaddingBelow
                    hasGutters
                >
                    {joinResults.map((result) => (
                        <ActivitiesExperiencesItem
                            searchResult={result}
                            key={result.id}
                        />
                    ))}
                </Carousel>
            )}
            {joinResults.length > 5 && (
                <Button
                    appearance="link"
                    onClick={() =>
                        navToSearch(
                            location.name,
                            [
                                'ticketfacility',
                                'timedentry',
                                'timedentry_tour',
                                'permits',
                                'vehiclepermit',
                                'activitypass',
                                'treepermit',
                            ],
                            { entity_id: location.locationId },
                            location.locationId
                        )
                    }
                    iconAfterElement={<Icon iconName="arrow-forward" />}
                >
                    View all {joinResults.length} Activities & Experiences
                </Button>
            )}
        </Box>
    );
}

ActivitiesExperiences.propTypes = propTypes;

function mapStateToProps(state) {
    return {
        search: fetchTicketSearchSelector(state),
        searchActivityPasses: fetchActivityPassesSearchSelector(state),
        searchVenues: fetchVenueReservationSelector(state),
        searchVenueFacilities: fetchVenueFacilitySelector(state),
        searchTreePermits: fetchTreePermitsSearchSelector(state),
    };
}

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            navToSearch: SearchActions.submitSimpleSearch,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(ActivitiesExperiences);
